/**
 * Tier-gated enemy AI (ADR-0016 / HEX-61).
 *
 * `chooseEnemyAssignment` is a **pure selector** over a `CardCombatState`: given
 * the enemy's freshly-rolled pool and both fully-visible combatants (ADR-0015 §4
 * symmetry), it returns the set of `CardAssignment`s the enemy should commit this
 * turn. The engine then applies each through its normal `doAssign` path, so every
 * assignment the selector proposes is one it has already checked is fundable — the
 * engine remains the sole validator (ADR-0016 §2).
 *
 * The §11.3 cadence makes this a *single-turn* optimisation against a frozen
 * opponent (the player neither rolls nor plays on the enemy's turn), so there is
 * no game tree — only a bounded assignment search (ADR-0016 §3).
 *
 * The ladder (ADR-0016 §4), each rung adding exactly one capability:
 *   - **common**   — greedy-valid. The HEX-52 placeholder verbatim: walk the
 *                    loadout row-major, feed each fundable card the lowest-index
 *                    dice that satisfy its cost. No scoring. Absent `aiProfile`
 *                    falls here, so legacy fixtures + the player are unchanged.
 *   - **rare**     — greedy damage-max. Maximise immediate expected damage to the
 *                    player; no block / status / trap valuation (a brute).
 *   - **epic**     — board-optimal vs the visible player. Full single-turn board
 *                    heuristic (damage + block + status + self-cost). Trap-blind.
 *   - **legendary** — epic's board score, minus the punishment of triggering the
 *                    player's visible traps (e.g. attacking into an armed
 *                    Caltrops), plus the value of arming its own feedable traps.
 *
 * Determinism (ADR-0016 §5): scoring is closed-form expectation over the uniform
 * d6 face distribution — no sampling, no RNG. The search order is deterministic,
 * so identical input state yields an identical assignment; the engine's own RNG
 * stream (fresh rolls at resolution) is untouched here.
 */

import type { CardAssignment, CardCombatState } from './cardEngine';
import { matches } from './predicates';
import { query } from './status';
import type {
  AiTier,
  Card,
  CardEffect,
  CardId,
  Combatant,
  DieFace,
  GridPlacement,
  Predicate,
} from './types';

// --------------------------------------------------------------------------
// Tuning constants — single-turn board heuristic weights (ADR-0016 §4)
// --------------------------------------------------------------------------

/** Weaken value per (magnitude × duration) point: tempo, worth less than raw HP. */
const WEAKEN_WEIGHT = 0.6;
/** Block value per useful point (block beyond the player's threat is wasted). */
const BLOCK_WEIGHT = 0.8;
/** Bonus for an assignment whose effective damage finishes the player this turn. */
const LETHAL_BONUS = 100;
/** Penalty (legendary) when triggering a player trap would itself kill the enemy. */
const SELF_LETHAL_PENALTY = 100;

/**
 * Hard branch cap for the epic / legendary enumerator (ADR-0016 §5). The search
 * tree is tiny for real enemy grids (≤ a few playable cards on ≤ 5 dice → true
 * optimal), but a pathological full grid is bounded by aborting at the cap and
 * falling back to a greedy beam — still single-pass, still valid.
 */
export const ENEMY_AI_BRANCH_CAP = 4000;

/** Optional search introspection for the compute-bound test (ADR-0016 §5). */
export type SearchStats = {
  /** Nodes visited by the enumerator (never exceeds {@link ENEMY_AI_BRANCH_CAP}). */
  evaluated: number;
  /** Whether the cap was hit and the greedy-beam fallback produced the result. */
  cappedOut: boolean;
};

// --------------------------------------------------------------------------
// Public selector
// --------------------------------------------------------------------------

/**
 * Choose the enemy's card→dice assignments for this turn (ADR-0016 §2). Pure:
 * reads `state`, returns a fresh assignment list, mutates nothing. `stats`, when
 * supplied, is filled with the search bound for the compute-bound test.
 */
export function chooseEnemyAssignment(
  state: CardCombatState,
  stats?: SearchStats,
): CardAssignment[] {
  if (stats !== undefined) {
    stats.evaluated = 0;
    stats.cappedOut = false;
  }
  const enemy = state.enemy;
  const tier: AiTier = enemy.aiProfile ?? 'common';
  const dice = freeDice(state);

  if (tier === 'common') return greedyValid(enemy, dice);

  const opts: ScoreOpts =
    tier === 'rare'
      ? { scorer: immediateDamageScore, trapAware: false }
      : { scorer: boardScore, trapAware: tier === 'legendary' };
  return enumerateBest(state, dice, opts, stats);
}

/**
 * Choose the *player's* card→dice assignments for an auto-played turn (HEX-148).
 *
 * The selector ({@link chooseEnemyAssignment}) reads `state.enemy` as the acting
 * actor and `state.player` as the opponent. The engine is fully symmetric
 * (ADR-0015 §4), so to drive the player side we hand the selector a view with the
 * two combatants swapped and the now-acting player given a strong `aiProfile`. The
 * returned assignments reference the player's own card ids and the live pool
 * indices, so they apply unchanged through `assignDie` on the real player turn — no
 * forked AI, the same ladder + scoring as the enemy uses (ADR-0016).
 *
 * `tier` defaults to legendary so Auto plays at full strength and never throws a
 * winnable fight (HEX-148 DoD). `state.pool` (the live rolled pool) is shared with
 * the swapped view, so the chosen dice indices are valid on the unswapped state.
 */
export function choosePlayerAssignment(
  state: CardCombatState,
  tier: AiTier = 'legendary',
  stats?: SearchStats,
): CardAssignment[] {
  const swapped: CardCombatState = {
    ...state,
    player: state.enemy,
    enemy: { ...state.player, aiProfile: tier },
  };
  return chooseEnemyAssignment(swapped, stats);
}

// --------------------------------------------------------------------------
// Dice / card view helpers
// --------------------------------------------------------------------------

type Avail = { index: number; face: DieFace };

function freeDice(state: CardCombatState): Avail[] {
  return state.pool
    .filter((slot) => slot.assignedTo === null)
    .map((slot) => ({ index: slot.index, face: slot.face }));
}

/** Loadout in row-major resolution order (matches the engine's `resolveTurn`). */
function rowMajor(placements: readonly GridPlacement[]): GridPlacement[] {
  return [...placements].sort((a, b) => a.origin.row - b.origin.row || a.origin.col - b.origin.col);
}

/** Whether a card may receive a die this turn for `enemy` — not frozen, not a
 * spent once-per-combat card. (Type-specific eligibility is decided per caller.) */
function notBlocked(enemy: Combatant, card: Card): boolean {
  if ((enemy.frozenCards.get(card.id) ?? 0) > 0) return false;
  if (card.oncePerCombat === true && enemy.oncePerCombatFired.has(card.id)) return false;
  return true;
}

// --------------------------------------------------------------------------
// common — greedy-valid (HEX-52 placeholder, preserved verbatim)
// --------------------------------------------------------------------------

/**
 * The placeholder behaviour, lifted unchanged so absent-`aiProfile` enemies and
 * every existing fixture resolve exactly as before: row-major, feed each fundable
 * card (incl. loaded/charged-fed-die traps) the lowest-index dice satisfying its
 * cost predicates. Passive traps and die cards (`cost.length === 0`) are skipped.
 */
function greedyValid(enemy: Combatant, dice: Avail[]): CardAssignment[] {
  const used = new Set<number>();
  const assignments: CardAssignment[] = [];
  for (const placement of rowMajor(enemy.inventory.loadout.placements)) {
    const card = placement.card;
    if (!notBlocked(enemy, card)) continue;
    if (card.cost.length === 0) continue; // passive trap / die card / free card
    const chosen = greedyMatch(dice, used, card.cost);
    if (chosen === null) continue;
    for (const i of chosen) used.add(i);
    assignments.push({ cardId: card.id, dieIndices: chosen });
  }
  return assignments;
}

/** Lowest-index die per predicate, one die per predicate, or null if impossible. */
function greedyMatch(
  dice: readonly Avail[],
  used: ReadonlySet<number>,
  predicates: readonly Predicate[],
): number[] | null {
  const taken = new Set<number>(used);
  const chosen: number[] = [];
  for (const pred of predicates) {
    const hit = dice.find((d) => !taken.has(d.index) && matches(d.face, pred));
    if (hit === undefined) return null;
    taken.add(hit.index);
    chosen.push(hit.index);
  }
  return chosen;
}

// --------------------------------------------------------------------------
// rare / epic / legendary — scored assignment search
// --------------------------------------------------------------------------

type ScoreOpts = {
  /** Maps an aggregated outcome to a scalar (rare = damage-only; epic/legendary
   * = full board). */
  scorer: (state: CardCombatState, agg: Outcome) => number;
  /** Legendary only: subtract player-trap punishment, add own-trap arm value. */
  trapAware: boolean;
};

/** A concrete play of one card: which dice it consumes and its analytic outcome.
 * `dieIndices` always lists cost dice first so each assignment prefix the engine
 * sees is itself valid (extra dice can only follow a satisfied cost). */
type Play = { cardId: CardId; dieIndices: number[]; outcome: Outcome };

/**
 * Expected (closed-form) result of a play / set of plays, all from the enemy's
 * point of view (ADR-0016 §4). Damage / block / status accumulate additively;
 * the scalar score folds in the player's standing block, the enemy's own weaken,
 * and tier-specific weights.
 */
type Outcome = {
  /** Expected raw damage aimed at the player (pre player-block, pre enemy-weaken). */
  dmgOpp: number;
  /** Expected number of damaging hits on the player (for per-hit weaken / block). */
  hitsOpp: number;
  /** Expected raw self-damage (Bloodletting cost, Roulette odd faces, …). */
  dmgSelf: number;
  /** Expected block gained by the enemy. */
  blockSelf: number;
  /** Expected HP the enemy heals on itself (HEX-89 Salve). Valued capped at the
   * enemy's missing HP — overheal is wasted. */
  healSelf: number;
  /** Expected poison dealt to the player as total HP (magnitude × duration). */
  poisonOpp: number;
  /** Expected weaken on the player as magnitude × duration (tempo). */
  weakenOpp: number;
  poisonSelf: number;
  weakenSelf: number;
  /** True if the play deals any damage to the player (Caltrops awareness). */
  attacksOpp: boolean;
  /** True if the play applies any status to the player (Mirror awareness). */
  appliesStatusOpp: boolean;
  /** Legendary: heuristic value of arming/charging the enemy's own trap. */
  ownTrapArm: number;
};

function zeroOutcome(): Outcome {
  return {
    dmgOpp: 0,
    hitsOpp: 0,
    dmgSelf: 0,
    blockSelf: 0,
    healSelf: 0,
    poisonOpp: 0,
    weakenOpp: 0,
    poisonSelf: 0,
    weakenSelf: 0,
    attacksOpp: false,
    appliesStatusOpp: false,
    ownTrapArm: 0,
  };
}

function aggregate(plays: readonly Play[]): Outcome {
  const agg = zeroOutcome();
  for (const p of plays) {
    const o = p.outcome;
    agg.dmgOpp += o.dmgOpp;
    agg.hitsOpp += o.hitsOpp;
    agg.dmgSelf += o.dmgSelf;
    agg.blockSelf += o.blockSelf;
    agg.healSelf += o.healSelf;
    agg.poisonOpp += o.poisonOpp;
    agg.weakenOpp += o.weakenOpp;
    agg.poisonSelf += o.poisonSelf;
    agg.weakenSelf += o.weakenSelf;
    agg.attacksOpp = agg.attacksOpp || o.attacksOpp;
    agg.appliesStatusOpp = agg.appliesStatusOpp || o.appliesStatusOpp;
    agg.ownTrapArm += o.ownTrapArm;
  }
  return agg;
}

/**
 * Enumerate assignments by DFS over the playable loadout (skip each card or give
 * it one fundable play, dice disjoint), scoring each leaf and keeping the max.
 * Bounded by {@link ENEMY_AI_BRANCH_CAP}; on overflow, abandon and fall back to a
 * greedy beam (ADR-0016 §5). Deterministic — no RNG, fixed traversal order.
 */
function enumerateBest(
  state: CardCombatState,
  dice: Avail[],
  opts: ScoreOpts,
  stats: SearchStats | undefined,
): CardAssignment[] {
  const cards = enumerableCards(state.enemy, opts.trapAware);
  let best: Play[] = [];
  let bestScore = Number.NEGATIVE_INFINITY;
  let nodes = 0;
  let cappedOut = false;

  const dfs = (i: number, free: Avail[], chosen: Play[]): void => {
    if (cappedOut) return;
    if (nodes >= ENEMY_AI_BRANCH_CAP) {
      cappedOut = true;
      return;
    }
    nodes += 1;
    if (i === cards.length) {
      const score = scoreAssignment(state, chosen, opts);
      if (score > bestScore) {
        bestScore = score;
        best = chosen;
      }
      return;
    }
    // Branch 1: skip card i.
    dfs(i + 1, free, chosen);
    if (cappedOut) return;
    // Branch 2..n: each fundable play of card i, with its dice removed.
    for (const play of cardPlays(cards[i] as GridPlacement, free, opts.trapAware)) {
      const used = new Set(play.dieIndices);
      dfs(
        i + 1,
        free.filter((d) => !used.has(d.index)),
        [...chosen, play],
      );
      if (cappedOut) return;
    }
  };

  dfs(0, dice, []);

  const plays = cappedOut ? greedyBeam(state, cards, dice, opts) : best;
  if (stats !== undefined) {
    stats.evaluated = nodes;
    stats.cappedOut = cappedOut;
  }
  return plays.map((p) => ({ cardId: p.cardId, dieIndices: p.dieIndices }));
}

/** Greedy width-1 fallback: repeatedly commit the single play that most improves
 * the score, until none helps. Bounded, deterministic, always valid. */
function greedyBeam(
  state: CardCombatState,
  cards: GridPlacement[],
  dice: Avail[],
  opts: ScoreOpts,
): Play[] {
  const chosen: Play[] = [];
  const usedCards = new Set<CardId>();
  let free = dice;
  let base = scoreAssignment(state, chosen, opts);
  for (;;) {
    let pick: Play | null = null;
    let pickGain = 1e-9; // require a real improvement
    for (const placement of cards) {
      if (usedCards.has(placement.card.id)) continue;
      for (const play of cardPlays(placement, free, opts.trapAware)) {
        const gain = scoreAssignment(state, [...chosen, play], opts) - base;
        if (gain > pickGain) {
          pickGain = gain;
          pick = play;
        }
      }
    }
    if (pick === null) break;
    chosen.push(pick);
    usedCards.add(pick.cardId);
    const used = new Set(pick.dieIndices);
    free = free.filter((d) => !used.has(d.index));
    base += pickGain;
  }
  return chosen;
}

/** Loadout cards the scored search may play, row-major. Excludes die cards,
 * frozen / spent cards, and free (cost-less) non-trap cards. Feedable traps
 * (loaded / charged-fed-die) are included only for the trap-aware tier
 * (legendary), which alone values arming its own traps (ADR-0016 §4). */
function enumerableCards(enemy: Combatant, trapAware: boolean): GridPlacement[] {
  return rowMajor(enemy.inventory.loadout.placements).filter((p) => {
    const card = p.card;
    if (card.type === 'die') return false;
    if (!notBlocked(enemy, card)) return false;
    if (card.type === 'trap') return trapAware && isFeedableTrap(card);
    return card.cost.length > 0;
  });
}

/** A loaded or charged-by-fed-die trap — armed/charged by spending dice on the
 * owner's turn (ADR-0015 §3, §17). Passive and trigger-charged traps take no dice. */
function isFeedableTrap(card: Card): boolean {
  const trap = card.trap;
  if (trap === undefined || card.cost.length === 0) return false;
  if (trap.variant === 'loaded') return true;
  return trap.variant === 'charged' && (trap.chargeSource ?? 'fedDie') === 'fedDie';
}

/**
 * The candidate plays of one card from the currently-free dice. Normal cards get
 * one play per distinct extra-die count (so "concentrate vs spread" the §19
 * scaling is explored); cards without `extraCost` get a single play. Feedable
 * traps get one arm/charge play valued by `ownTrapArm`.
 */
function cardPlays(placement: GridPlacement, free: Avail[], trapAware: boolean): Play[] {
  const card = placement.card;
  const costIdx = matchCost(card.cost, free);
  if (costIdx === null) return [];

  if (card.type === 'trap') {
    if (!trapAware) return [];
    const outcome = zeroOutcome();
    outcome.ownTrapArm = trapArmValue(card);
    return [{ cardId: card.id, dieIndices: costIdx, outcome }];
  }

  const costFaces = costIdx.map((i) => faceAt(free, i));
  if (card.extraCost === undefined) {
    return [{ cardId: card.id, dieIndices: costIdx, outcome: evaluatePlay(card, costFaces, 0) }];
  }

  // Extra dice: any remaining free die matching `extraCost.predicate`, lowest
  // index first. One play per count fed (0..available) — each fires `perDie`.
  const costSet = new Set(costIdx);
  const extras = free.filter(
    (d) =>
      !costSet.has(d.index) &&
      matches(d.face, (card.extraCost as NonNullable<Card['extraCost']>).predicate),
  );
  const plays: Play[] = [];
  for (let k = 0; k <= extras.length; k++) {
    const extraIdx = extras.slice(0, k).map((d) => d.index);
    plays.push({
      cardId: card.id,
      dieIndices: [...costIdx, ...extraIdx],
      outcome: evaluatePlay(card, costFaces, k),
    });
  }
  return plays;
}

/** Backtracking cost match → die indices (cost-predicate order, lowest index
 * first), or null. Mirrors the engine's `hasMatching`, returning the witness. */
function matchCost(cost: readonly Predicate[], dice: readonly Avail[]): number[] | null {
  const used = new Array<boolean>(dice.length).fill(false);
  const chosen: number[] = [];
  const rec = (ci: number): boolean => {
    if (ci === cost.length) return true;
    const pred = cost[ci];
    if (pred === undefined) return false;
    for (let i = 0; i < dice.length; i++) {
      const d = dice[i];
      if (used[i] || d === undefined || !matches(d.face, pred)) continue;
      used[i] = true;
      chosen.push(d.index);
      if (rec(ci + 1)) return true;
      chosen.pop();
      used[i] = false;
    }
    return false;
  };
  return rec(0) ? chosen : null;
}

function faceAt(dice: readonly Avail[], index: number): DieFace {
  const d = dice.find((x) => x.index === index);
  if (d === undefined) throw new Error(`die index ${index} not free`);
  return d.face;
}

// --------------------------------------------------------------------------
// Analytic evaluation (closed-form expectation — ADR-0016 §4, §5)
// --------------------------------------------------------------------------

/** Evaluate a single non-trap play given its cost faces and the number of extra
 * dice fed. Recursive cards are scored by closed-form expectation over the
 * uniform face distribution — no sampling (ADR-0016 §4). */
function evaluatePlay(card: Card, costFaces: readonly DieFace[], extraCount: number): Outcome {
  const out = zeroOutcome();
  addEffects(out, card.effects, 1);
  const extra = card.extraCost;
  if (extra !== undefined) {
    for (let i = 0; i < extraCount; i++) addEffects(out, extra.perDie, 1);
  }
  if (card.costBonuses !== undefined) {
    for (const face of costFaces) {
      for (const bonus of card.costBonuses) {
        if (matches(face, bonus.facePredicate)) addEffects(out, bonus.effects, 1);
      }
    }
  }
  return out;
}

/** Accumulate a list of effects into `out`, each scaled by `weight` (an
 * expected-fire count for fresh-roll branches). */
function addEffects(
  out: Outcome,
  effects: readonly CardEffect[],
  weight: number,
  scope?: { faceValue?: number; faceSum?: number },
): void {
  for (const effect of effects) addEffect(out, effect, weight, scope);
}

function addEffect(
  out: Outcome,
  effect: CardEffect,
  weight: number,
  scope?: { faceValue?: number; faceSum?: number },
): void {
  switch (effect.kind) {
    case 'damage': {
      const amt = magnitude(effect.amount, effect.amountFrom, scope);
      applyTargetedDamage(out, effect.target ?? 'opponent', amt, weight);
      break;
    }
    case 'block': {
      const amt = magnitude(effect.amount, effect.amountFrom, scope);
      // Block defaults to self; opponent-targeted block is not a meaningful play.
      if ((effect.target ?? 'self') === 'self') out.blockSelf += amt * weight;
      break;
    }
    case 'applyStatus': {
      const mag = magnitude(effect.application.magnitude, effect.magnitudeFrom, scope);
      const total = mag * effect.application.duration;
      const target = effect.target ?? effect.application.target ?? 'opponent';
      applyTargetedStatus(out, target, effect.application.status, total, weight);
      break;
    }
    case 'rollFresh': {
      addRollFresh(out, effect, weight, scope);
      break;
    }
    case 'heal': {
      // Self-heal is the only meaningful play; healing the player is anti-value.
      if ((effect.target ?? 'self') === 'self') out.healSelf += effect.amount * weight;
      break;
    }
    // No score contribution: reroll is pool churn; armOrCharge is handled as a
    // trap-arm play; trap-context effects only resolve while a trap fires.
    case 'rerollDice':
    case 'armOrCharge':
    case 'reflectDamage':
    case 'reduceBlock':
    case 'mirrorStatus':
    case 'freezeCard':
      break;
  }
}

function applyTargetedDamage(
  out: Outcome,
  target: 'opponent' | 'self' | 'random',
  amount: number,
  weight: number,
): void {
  if (amount <= 0) return;
  if (target === 'opponent') {
    out.dmgOpp += amount * weight;
    out.hitsOpp += weight;
    out.attacksOpp = true;
  } else if (target === 'self') {
    out.dmgSelf += amount * weight;
  } else {
    // random: 50/50 enemy/self (ADR-0015 §17).
    out.dmgOpp += amount * weight * 0.5;
    out.hitsOpp += weight * 0.5;
    out.attacksOpp = true;
    out.dmgSelf += amount * weight * 0.5;
  }
}

function applyTargetedStatus(
  out: Outcome,
  target: 'opponent' | 'self' | 'random',
  status: 'poison' | 'weaken',
  total: number,
  weight: number,
): void {
  if (total <= 0) return;
  const toOpp = (w: number) => {
    if (status === 'poison') out.poisonOpp += total * w;
    else out.weakenOpp += total * w;
    out.appliesStatusOpp = true;
  };
  const toSelf = (w: number) => {
    if (status === 'poison') out.poisonSelf += total * w;
    else out.weakenSelf += total * w;
  };
  if (target === 'opponent') toOpp(weight);
  else if (target === 'self') toSelf(weight);
  else {
    toOpp(weight * 0.5);
    toSelf(weight * 0.5);
  }
}

/** Closed-form expectation of a `rollFresh`: each `perFace` branch fires an
 * expected `count × P(face matches)` times with `faceValue = E[face | matches]`;
 * `onSum` fires once with `faceSum = count × 3.5` (ADR-0016 §4). */
function addRollFresh(
  out: Outcome,
  effect: Extract<CardEffect, { kind: 'rollFresh' }>,
  weight: number,
  scope?: { faceValue?: number; faceSum?: number },
): void {
  void scope; // a rollFresh is always top-level (depth-1 cap) — no inherited scope
  if (effect.perFace !== undefined) {
    for (const branch of effect.perFace) {
      const { p, e } = predicateStats(branch.facePredicate);
      const fires = effect.count * p;
      if (fires <= 0) continue;
      addEffects(out, branch.effects, weight * fires, { faceValue: e });
    }
  }
  if (effect.onSum !== undefined) {
    addEffects(out, effect.onSum, weight, { faceSum: effect.count * 3.5 });
  }
}

/** Read an effect magnitude: the literal, or the fresh-roll face scope. */
function magnitude(
  base: number,
  from: 'faceValue' | 'faceSum' | undefined,
  scope?: { faceValue?: number; faceSum?: number },
): number {
  if (from === undefined) return base;
  if (from === 'faceValue') return scope?.faceValue ?? base;
  return scope?.faceSum ?? base;
}

/** Probability a single d6 face satisfies `pred`, and the conditional mean face
 * given it does. Used to expand `rollFresh` analytically. */
function predicateStats(pred: Predicate): { p: number; e: number } {
  switch (pred.kind) {
    case 'min': {
      const lo = Math.min(6, Math.max(1, pred.value));
      return { p: (6 - lo + 1) / 6, e: (lo + 6) / 2 };
    }
    case 'max': {
      const hi = Math.max(1, Math.min(6, pred.value));
      return { p: hi / 6, e: (1 + hi) / 2 };
    }
    case 'exact':
      return pred.value >= 1 && pred.value <= 6 ? { p: 1 / 6, e: pred.value } : { p: 0, e: 0 };
    case 'parity':
      return pred.value === 'even' ? { p: 0.5, e: 4 } : { p: 0.5, e: 3 };
  }
}

// --------------------------------------------------------------------------
// Scoring (ADR-0016 §4)
// --------------------------------------------------------------------------

function scoreAssignment(state: CardCombatState, plays: readonly Play[], opts: ScoreOpts): number {
  const agg = aggregate(plays);
  let score = opts.scorer(state, agg);
  if (opts.trapAware) {
    score -= trapPunishment(state, agg);
    score += agg.ownTrapArm;
  }
  return score;
}

/** Damage that actually lands on the player: enemy weaken reduces each hit, the
 * player's standing block soaks the rest (ADR-0008 §6 — block persists into the
 * enemy's turn). */
function effectiveDamageToPlayer(state: CardCombatState, agg: Outcome): number {
  const enemyWeaken = query(state.enemy.statusBag, 'weaken');
  const raw = Math.max(0, agg.dmgOpp - enemyWeaken * agg.hitsOpp);
  return Math.max(0, raw - state.player.block);
}

/** rare: immediate damage only — no block / status / self valuation (ADR-0016 §4). */
function immediateDamageScore(state: CardCombatState, agg: Outcome): number {
  return effectiveDamageToPlayer(state, agg);
}

/** epic / legendary: full single-turn board value (ADR-0016 §4). */
function boardScore(state: CardCombatState, agg: Outcome): number {
  const eff = effectiveDamageToPlayer(state, agg);
  let score =
    eff +
    agg.poisonOpp +
    WEAKEN_WEIGHT * agg.weakenOpp +
    blockValue(state, agg.blockSelf) +
    healValue(state, agg.healSelf) -
    agg.dmgSelf -
    agg.poisonSelf -
    WEAKEN_WEIGHT * agg.weakenSelf;
  if (eff >= state.player.hp) score += LETHAL_BONUS; // close the gap when able
  return score;
}

/** Block is worth up to the player's estimated next-turn damage; armour beyond
 * what the player can throw is wasted (ADR-0016 §4 "spends dice on defence when
 * ahead, offence when it can close the gap"). */
function blockValue(state: CardCombatState, block: number): number {
  if (block <= 0) return 0;
  const useful = Math.min(block, estimateThreat(state.player));
  return BLOCK_WEIGHT * useful;
}

/** Self-heal is worth the HP it actually restores — overheal past maxHp is
 * wasted (HEX-89). Valued 1:1 with HP, so a wounded enemy weighs a Salve against
 * the equivalent block / damage on the same dice. */
function healValue(state: CardCombatState, heal: number): number {
  if (heal <= 0) return 0;
  const missing = state.enemy.maxHp - state.enemy.hp;
  return Math.min(heal, Math.max(0, missing));
}

/** Rough estimate of the player's reachable damage next turn: the nominal
 * opponent-damage of their playable cards (funding ignored — a ceiling). */
function estimateThreat(player: Combatant): number {
  let threat = 0;
  for (const placement of player.inventory.loadout.placements) {
    const card = placement.card;
    if (card.type === 'die' || card.type === 'trap' || card.cost.length === 0) continue;
    const out = evaluatePlay(card, [], extraCeiling(card));
    threat += out.dmgOpp + out.poisonOpp;
  }
  return threat;
}

/** Upper bound on extra dice a card could be fed (for a threat ceiling). */
function extraCeiling(card: Card): number {
  return card.extraCost === undefined ? 0 : 3;
}

// --------------------------------------------------------------------------
// Legendary — player-trap awareness (ADR-0016 §4)
// --------------------------------------------------------------------------

/**
 * The expected cost of triggering the player's visible traps with this
 * assignment (ADR-0016 §4). Reads `state.player`'s loadout + trap runtime —
 * full symmetry means no hidden information. Currently models the three §14
 * punishing traps: Caltrops (reflect), Spike Wall (block reduction), Mirror
 * (status mirror). A reflection that would itself kill the enemy is weighted as
 * near-prohibitive.
 */
function trapPunishment(state: CardCombatState, agg: Outcome): number {
  let pen = 0;
  for (const placement of state.player.inventory.loadout.placements) {
    const card = placement.card;
    const trap = card.trap;
    if (trap === undefined) continue;
    if (!trapWouldFire(state, card, agg)) continue;
    for (const effect of card.effects) {
      if (effect.kind === 'reflectDamage') {
        const back = Math.floor(effect.fraction * agg.dmgOpp);
        pen += back;
        if (back >= state.enemy.hp) pen += SELF_LETHAL_PENALTY; // never suicide into it
      } else if (effect.kind === 'reduceBlock') {
        pen += Math.min(agg.blockSelf, effect.amount) * BLOCK_WEIGHT;
      } else if (effect.kind === 'mirrorStatus') {
        pen += agg.poisonOpp + WEAKEN_WEIGHT * agg.weakenOpp; // statuses bounced back
      }
    }
  }
  return pen;
}

/** Whether a player trap would fire in response to this assignment, given its
 * variant + runtime + the assignment's action tags. Roll-face / turn-end traps
 * are not assignment-driven, so they are not modelled here. */
function trapWouldFire(state: CardCombatState, card: Card, agg: Outcome): boolean {
  const trap = card.trap;
  if (trap === undefined) return false;
  const runtime = state.player.trapState.get(card.id);
  if (trap.variant === 'loaded' && !(runtime?.armed ?? false)) return false;
  if (trap.variant === 'charged' && (runtime?.charges ?? 0) <= 0) return false;
  switch (trap.trigger.kind) {
    case 'opponentAttacks':
      return agg.attacksOpp;
    case 'opponentBlocks':
      return agg.blockSelf > 0;
    case 'opponentAppliesStatus': {
      if (!agg.appliesStatusOpp) return false;
      // The enemy applying poison/weaken is enough; a status-specific mirror is
      // matched conservatively (treated as firing) since we don't split the bag.
      return true;
    }
    default:
      return false; // opponentRollFace / turnEnd — not assignment-driven
  }
}

/** Heuristic value of arming/charging the enemy's own feedable trap this turn. */
function trapArmValue(card: Card): number {
  for (const effect of card.effects) {
    if (effect.kind === 'reflectDamage') return 4;
    if (effect.kind === 'reduceBlock') return 3;
  }
  return 2;
}
